import { logger, notify } from '@/shared';

/**
 * Install global error handlers for uncaught errors and unhandled promise rejections.
 * These catch errors that React error boundaries cannot:
 * - Event handler errors
 * - Async errors (setTimeout, promises)
 * - Errors outside React's render tree
 *
 * Call this once during application startup (before rendering).
 */
export function installGlobalErrorHandlers(): void {
  // Handle synchronous errors
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): void => {
    logger.error(
      'Uncaught error',
      {
        surface: 'global',
        operation: 'window.onerror',
        message: typeof message === 'string' ? message : 'Unknown error',
        source,
        lineno,
        colno,
      },
      error ?? new Error(typeof message === 'string' ? message : 'Unknown error')
    );

    notify.error('Unexpected error', error);
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    const error = reason instanceof Error ? reason : undefined;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';

    logger.error(
      'Unhandled promise rejection',
      {
        surface: 'global',
        operation: 'window.onunhandledrejection',
        message,
        unhandledRejection: true,
      },
      error ?? new Error(message)
    );

    notify.error('Unexpected error', error);
  };
}
